import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Routes, Route, Link, useNavigate, useMatch } from 'react-router-dom';
import styled from 'styled-components';
import { courseService } from '../../api/courseService';
import CourseForm from './CourseForm';
import LessonManagement from './LessonManagement';
import type { Course } from '../../api/courseService';

const CourseManagementContainer = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 0.25rem;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
  &.delete {
    border-color: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.error};
    &:hover {
      background-color: ${({ theme }) => theme.colors.error};
      color: white;
    }
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.errorLight};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const CourseManagement: React.FC = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isLessonsRoute = useMatch('/admin/courses/:courseId/lessons/*');

  // Fetch courses
  const { data: coursesResponse, isLoading, error } = useQuery({
    queryKey: ['adminCourses'],
    queryFn: () => courseService.getCourses(),
  });

  // Create course mutation
  const createMutation = useMutation({
    mutationFn: (courseData: { title: string; description: string; thumbnail_url?: string }) => 
      courseService.createCourse(courseData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCourses'] });
      setIsCreating(false);
    },
  });

  // Update course mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{ title: string; description: string; thumbnail_url?: string; is_published?: boolean }> }) => 
      courseService.updateCourse(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCourses'] });
      setIsEditing(false);
      setSelectedCourse(null);
    },
  });

  // Delete course mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => courseService.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCourses'] });
    },
  });

  const handleCreateCourse = (data: { title: string; description: string; thumbnail_url?: string }) => {
    createMutation.mutate(data);
  };

  const handleUpdateCourse = (data: { title: string; description: string; thumbnail_url?: string; is_published?: boolean }) => {
    if (selectedCourse) {
      updateMutation.mutate({ id: selectedCourse.id, data });
    }
  };

  const handleDeleteCourse = (id: string) => {
    if (window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleManageLessons = (courseId: string) => {
    navigate(`/admin/courses/${courseId}/lessons`);
  };

  const courses = coursesResponse?.data?.data || [];

  if (isLoading) {
    return <LoadingMessage>Loading courses...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error loading courses: {(error as Error).message}</ErrorMessage>;
  }

  if (isLessonsRoute) {
    // Render only LessonManagement with a back button
    return (
      <CourseManagementContainer>
        <Button onClick={() => navigate('/admin')} style={{ marginBottom: '1rem' }}>
          &larr; Back to Courses
        </Button>
        <LessonManagement />
      </CourseManagementContainer>
    );
  }

  return (
    <CourseManagementContainer>
      <Header>
        <h2>Course Management</h2>
        <Button onClick={() => setIsCreating(true)}>Create New Course</Button>
      </Header>

      {isCreating && (
        <CourseForm
          onSubmit={handleCreateCourse}
          onCancel={() => setIsCreating(false)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {isEditing && selectedCourse && (
        <CourseForm
          course={selectedCourse}
          onSubmit={handleUpdateCourse}
          onCancel={() => {
            setIsEditing(false);
            setSelectedCourse(null);
          }}
          isSubmitting={updateMutation.isPending}
        />
      )}

      <Table>
        <thead>
          <tr>
            <Th>Title</Th>
            <Th>Description</Th>
            <Th>Status</Th>
            <Th>Lessons</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {courses.map((course) => (
            <tr key={course.id}>
              <Td>{course.title}</Td>
              <Td>{course.description.length > 50 ? `${course.description.substring(0, 50)}...` : course.description}</Td>
              <Td>{course.is_published ? 'Published' : 'Draft'}</Td>
              <Td>{course.lesson_count || 0}</Td>
              <Td>
                <ActionButton
                  onClick={() => {
                    setSelectedCourse(course);
                    setIsEditing(true);
                  }}
                >
                  Edit
                </ActionButton>
                <ActionButton onClick={() => handleManageLessons(course.id)}>
                  Lessons
                </ActionButton>
                <ActionButton
                  className="delete"
                  onClick={() => handleDeleteCourse(course.id)}
                >
                  Delete
                </ActionButton>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </CourseManagementContainer>
  );
};

export default CourseManagement; 